import { z } from 'zod'

import {
  type IdentityProviderRecord,
  identityProviderRecordSchema,
  identityProviders,
} from '@scalar-org/entities/auth'
import { nanoid, slugSchema } from '@scalar-org/entities/utility'

import type { RequestService } from '../request'

export function authApiFactory(requestService: RequestService) {
  /** Request a password reset for a given email */
  async function requestPasswordReset(email: string) {
    return await requestService.request({
      url: 'core/login/request-password-reset',
      method: 'post',
      data: {
        email,
        clientId: nanoid(),
      },
      schema: z.null(),
      disableAuth: true,
    })
  }

  /** Reset a password using an email-link token */
  async function resetPassword(
    password: string,
    clientId: string,
    resetToken: string,
  ) {
    return await requestService.request({
      url: 'core/login/reset-password',
      method: 'post',
      data: {
        clientId,
        resetToken,
        password,
      },
      disableAuth: true,
      schema: z.object({
        accessToken: z.string(),
        refreshToken: z.string(),
      }),
    })
  }

  async function generatePersonalToken({
    name,
    description,
    refreshToken,
  }: {
    name: string
    description?: string
    refreshToken: string
  }) {
    return await requestService.request({
      url: 'core/login/personal-token/generate',
      method: 'post',
      data: {
        name,
        description,
        refreshToken,
      },
      schema: z.object({
        uid: z.string(),
        personalToken: z.string(),
      }),
    })
  }

  async function getAccessTokenByPersonalToken({
    personalToken,
  }: {
    personalToken: string
  }) {
    return await requestService.request({
      url: 'core/login/personal-token/access',
      method: 'post',
      data: {
        personalToken,
      },
      schema: z.object({
        accessToken: z.string(),
      }),
    })
  }

  async function revokePersonalToken({ uid }: { uid: string }) {
    return await requestService.request({
      url: `core/login/personal-token/${uid}/revoke`,
      method: 'post',
      schema: z.null(),
    })
  }

  async function deletePersonalToken({ uid }: { uid: string }) {
    return await requestService.request({
      url: `core/login/personal-token/${uid}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  async function getIdentityProvider(uid: string) {
    return await requestService.request({
      url: `core/identity-provider/${uid}`,
      method: 'get',
      schema: identityProviderRecordSchema,
    })
  }

  async function createIdentityProvider(
    data: Pick<IdentityProviderRecord, 'name' | 'provider' | 'consumerOnly'> &
      Partial<
        Pick<
          IdentityProviderRecord,
          'issuer' | 'x509Cert' | 'ssoUrl' | 'createUsers' | 'signRequests'
        >
      >,
  ) {
    return await requestService.request({
      url: 'core/identity-provider',
      method: 'post',
      data,
      schema: identityProviderRecordSchema,
    })
  }

  async function updateIdentityProvider(
    uid: string,
    data: Partial<
      Pick<
        IdentityProviderRecord,
        | 'issuer'
        | 'name'
        | 'ssoUrl'
        | 'x509Cert'
        | 'createUsers'
        | 'signRequests'
      >
    >,
  ) {
    return await requestService.request({
      url: 'core/identity-provider/update',
      method: 'post',
      data: {
        uid,
        ...data,
      },
      schema: identityProviderRecordSchema,
    })
  }

  async function deleteIdentityProvider(uid: string) {
    return await requestService.request({
      url: `core/identity-provider/${uid}`,
      method: 'delete',
      schema: z.null(),
    })
  }

  async function initiateSaml(data: { email?: string; domain?: string }) {
    return await requestService.request({
      url: 'core/saml/initiate',
      method: 'post',
      data,
      schema: z.object({
        options: z
          .object({
            loginUrl: z.url(),
            provider: z.enum(identityProviders),
            teamSlug: slugSchema,
          })
          .array(),
      }),
    })
  }

  /** Change password for authenticated user */
  async function changePassword({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string
    newPassword: string
  }) {
    return await requestService.request({
      url: 'core/login/change-password',
      method: 'post',
      data: {
        oldPassword,
        newPassword,
      },
      schema: z.null(),
    })
  }

  return {
    requestPasswordReset,
    resetPassword,
    changePassword,

    // Personal tokens
    generatePersonalToken,
    getAccessTokenByPersonalToken,
    revokePersonalToken,
    deletePersonalToken,

    // Identity Provider,
    getIdentityProvider,
    createIdentityProvider,
    updateIdentityProvider,
    deleteIdentityProvider,

    // SAML
    initiateSaml,
  }
}
